import { useEffect, useRef, useState } from "react"

import type { Annotation } from "@/types"
import { cn, Label, Textarea, textareaVariants, Tooltip } from "@coval/ui"
import { Info, Pencil } from "lucide-react"

const NOTE_INFO_TEXT =
  "Your note is saved with this review and used as feedback to help improve the metric's scoring over time."

const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
  if (textarea == null) {
    return
  }
  textarea.style.height = "auto"
  textarea.style.height = `${textarea.scrollHeight}px`
}

interface InlineCommentBoxProps {
  annotation: Annotation | null
  onExplanationChange: (
    explanation: string | null,
  ) => false | undefined | Promise<boolean>
}

export const InlineCommentBox: React.FC<InlineCommentBoxProps> = ({
  annotation,
  onExplanationChange,
}) => {
  const savedNote = annotation?.annotations?.custom_explanation ?? ""
  const annotationId = annotation?.id ?? null
  const [isEditing, setIsEditing] = useState(false)
  const [commentText, setCommentText] = useState(savedNote)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const skipCommitRef = useRef(false)
  const annotationResetInProgressRef = useRef(false)
  const isEditingRef = useRef(false)
  const commentTextRef = useRef(commentText)
  const savedNoteRef = useRef(savedNote)
  const onExplanationChangeRef = useRef(onExplanationChange)
  const pendingCommittedNoteRef = useRef<{
    annotationId: string | null
    note: string
    previousSavedNote: string
  } | null>(null)
  const previousAnnotationIdRef = useRef<string | null>(annotationId)

  useEffect(() => {
    const annotationChanged = previousAnnotationIdRef.current !== annotationId
    previousAnnotationIdRef.current = annotationId

    if (annotationChanged) {
      skipCommitRef.current = true
      annotationResetInProgressRef.current = true
      isEditingRef.current = false
      pendingCommittedNoteRef.current = null
      setCommentText(savedNote)
      setIsEditing(false)
      return
    }

    if (isEditing) {
      return
    }

    const pendingCommittedNote = pendingCommittedNoteRef.current
    if (pendingCommittedNote !== null && !annotationChanged) {
      if (savedNote === pendingCommittedNote.note) {
        pendingCommittedNoteRef.current = null
      } else if (savedNote === pendingCommittedNote.previousSavedNote) {
        setCommentText(pendingCommittedNote.note)
        return
      } else {
        pendingCommittedNoteRef.current = null
      }
    }

    setCommentText(savedNote)
  }, [annotationId, savedNote, isEditing])

  useEffect(() => {
    isEditingRef.current = isEditing
    commentTextRef.current = commentText
    savedNoteRef.current = savedNote
    onExplanationChangeRef.current = onExplanationChange
  }, [commentText, isEditing, onExplanationChange, savedNote])

  useEffect(() => {
    if (!isEditing) {
      annotationResetInProgressRef.current = false
      return
    }

    if (annotationResetInProgressRef.current) {
      return
    }

    skipCommitRef.current = false
    const textarea = textareaRef.current
    if (textarea != null) {
      textarea.focus()
      textarea.setSelectionRange(textarea.value.length, textarea.value.length)
      autoResizeTextarea(textarea)
    }
  }, [isEditing])

  useEffect(() => {
    return () => {
      if (
        !isEditingRef.current ||
        skipCommitRef.current ||
        annotationResetInProgressRef.current
      ) {
        return
      }

      const trimmed = commentTextRef.current.trim()
      const currentSavedNote = savedNoteRef.current
      if (trimmed !== currentSavedNote) {
        void onExplanationChangeRef.current(trimmed)
      }
    }
  }, [])

  const commitNote = () => {
    isEditingRef.current = false
    if (skipCommitRef.current) {
      skipCommitRef.current = false
      return
    }
    const trimmed = commentText.trim()
    if (trimmed !== savedNote) {
      const saveAccepted = onExplanationChange(trimmed)
      if (saveAccepted === false) {
        setCommentText(savedNote)
        setIsEditing(false)
        return
      }
      if (saveAccepted === undefined) {
        setCommentText(savedNote)
        setIsEditing(false)
        return
      }

      const pendingCommittedNote = {
        annotationId,
        note: trimmed,
        previousSavedNote: savedNote,
      }
      pendingCommittedNoteRef.current = pendingCommittedNote
      const clearRejectedPendingNote = () => {
        if (
          pendingCommittedNoteRef.current?.annotationId ===
            pendingCommittedNote.annotationId &&
          pendingCommittedNoteRef.current?.note === pendingCommittedNote.note &&
          pendingCommittedNoteRef.current.previousSavedNote ===
            pendingCommittedNote.previousSavedNote
        ) {
          pendingCommittedNoteRef.current = null
          setCommentText(savedNote)
        }
      }
      void saveAccepted.then((accepted) => {
        if (!accepted) {
          clearRejectedPendingNote()
        }
      }, clearRejectedPendingNote)
    }
    setCommentText(trimmed)
    setIsEditing(false)
  }

  const cancelEditing = () => {
    skipCommitRef.current = true
    isEditingRef.current = false
    setCommentText(savedNote)
    setIsEditing(false)
  }

  const startEditing = () => {
    skipCommitRef.current = false
    annotationResetInProgressRef.current = false
    isEditingRef.current = true
    setIsEditing(true)
  }

  const displayNote = commentText
  const hasNote = displayNote !== ""
  // Resting and editing states share the standard text-input surface so the
  // note reads as a field in both, and swapping between them does not shift
  // the surrounding layout.
  const fieldClasses = cn(textareaVariants(), "min-h-0 resize-none px-3 py-2")

  return (
    <div>
      <div className="mb-3 flex items-center gap-1">
        <Label>Note</Label>
        <Tooltip content={NOTE_INFO_TEXT} side="top" className="max-w-64">
          <button
            type="button"
            className="inline-flex text-coval-text-muted transition-colors hover:text-coval-text"
            aria-label="Why leave a note?"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </Tooltip>
      </div>

      {isEditing ? (
        <Textarea
          ref={textareaRef}
          value={commentText}
          onChange={(e) => {
            const nextCommentText = e.target.value
            commentTextRef.current = nextCommentText
            setCommentText(nextCommentText)
            autoResizeTextarea(e.target)
          }}
          onBlur={commitNote}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              cancelEditing()
            } else if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              e.currentTarget.blur()
            }
          }}
          placeholder="Add a note…"
          aria-label="Note"
          rows={1}
          maxLength={500}
          className={cn(fieldClasses, "overflow-hidden")}
          data-note-textarea
        />
      ) : (
        <button
          type="button"
          onClick={startEditing}
          aria-label={hasNote ? "Edit note" : "Add a note"}
          data-testid="metric-note-button"
          className={cn(
            fieldClasses,
            "group/note flex items-start gap-2 text-left hover:border-coval-border-active",
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 whitespace-pre-wrap break-words",
              !hasNote && "text-coval-text-muted",
            )}
          >
            {hasNote ? displayNote : "Add a note…"}
          </span>
          <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-coval-text-muted opacity-0 transition-opacity group-hover/note:opacity-100" />
        </button>
      )}
    </div>
  )
}
